import {useRef, useEffect, useState, useCallback} from 'react'
import {Button} from '@primer/react'
import {clsx} from 'clsx'
import styles from './ExpandableContent.module.css'

const DEFAULT_MAX_HEIGHT = 256

interface ExpandableContentProps {
  /** Content rendered inside the expandable wrapper */
  children: React.ReactNode
  /** Additional class name applied to the outer wrapper */
  className?: string
  /** Section name to add to show more label for a11y */
  sectionLabel: string
  /** Max height in pixels before content is collapsed (default: 256) */
  maxHeight?: number
}

/** Wraps content with overflow detection, gradient fade, and "Show more" toggle */
export function ExpandableContent({
  children,
  sectionLabel,
  className,
  maxHeight = DEFAULT_MAX_HEIGHT,
}: ExpandableContentProps) {
  const bodyRef = useRef<HTMLDivElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  useEffect(() => {
    const node = bodyRef.current
    if (!node) return

    const checkOverflow = () => {
      setIsOverflowing(node.scrollHeight > maxHeight)
    }

    checkOverflow()

    const resizeObserver = new ResizeObserver(() => checkOverflow())
    resizeObserver.observe(node)

    return () => {
      resizeObserver.disconnect()
    }
  }, [maxHeight])

  const handleToggle = useCallback(() => {
    setIsExpanded(prev => !prev)
  }, [])

  const isCollapsed = isOverflowing && !isExpanded
  const expandedText = isExpanded ? 'Show less' : 'Show more'

  return (
    <div
      ref={bodyRef}
      className={clsx(styles.body, isCollapsed && styles.bodyCollapsed, className)}
      style={isCollapsed ? {maxHeight} : undefined}
      data-testid="expandable-content-body"
    >
      <div className={styles.content} inert={isCollapsed ? true : undefined} data-testid="expandable-content-inner">
        {children}
      </div>
      {isOverflowing && (
        <Button
          variant="default"
          size="medium"
          onClick={handleToggle}
          aria-expanded={isExpanded}
          aria-label={`${expandedText} ${sectionLabel}`}
          className={isCollapsed ? styles.toggleCollapsed : styles.toggleExpanded}
        >
          {expandedText}
        </Button>
      )}
    </div>
  )
}
